/*
  Node Mapping Utilities

  This module handles conversion between API nodes and document nodes,
  including field normalization and type transformations.
*/

import type { ApiNode, Node as DocNode } from '../../types';

/**
 * Normalize outcome objects to ensure they have required fields
 *
 * @param outcomes - Array of outcomes from API
 * @returns Normalized outcomes with labels
 */
function normalizeOutcomes(outcomes: ApiNode['outcomes']): DocNode['outcomes'] {
  if (!outcomes) return undefined;

  return outcomes
    .map(o => {
      const label = o.label ?? o.name ?? null;
      if (!label) return null;
      return { ...o, label };
    })
    .filter(Boolean) as DocNode['outcomes'];
}

/**
 * Normalize config for outcome nodes
 *
 * @param config - Original config object
 * @returns Normalized config with all required fields
 */
function normalizeOutcomeConfig(config: any): any {
  return {
    ...config,
    outcome: config.outcome ?? null,
    customer_outcome_id: null, // Always null from API, set during enrichment
    outcome_icon: null, // Always null from API, set during enrichment
    outcome_color: null, // Always null from API, set during enrichment
    terminal: config.terminal ?? null,
    reconciliation: config.reconciliation ?? null,
  };
}

/**
 * Normalize config for review nodes
 *
 * @param config - Original config object
 * @returns Normalized config with all required fields
 */
function normalizeReviewConfig(config: any): any {
  return {
    ...config,
    reconciliation: config.reconciliation ?? null,
    terminal: config.terminal ?? null,
    action_id: config.action_id ?? null,
    action_version_id: config.action_version_id ?? null,
    action_name: config.action_name ?? null,
    action_type: config.action_type ?? null,
    action_version: config.action_version ?? null,
  };
}

/**
 * Normalize config for reconciliation workflow nodes
 *
 * @param config - Original config object
 * @returns Normalized config with all required fields
 */
function normalizeReconciliationWorkflowConfig(config: any): any {
  return {
    ...config,
    terminal: config.terminal ?? null,
    legacy: config.legacy ?? null,
    workflow_token: config.workflow_token ?? null,
    workflow_type: config.workflow_type ?? null,
    workflow_version: config.application_version_id ?? null,
  };
}

/**
 * Normalize config for workflow and start_router nodes
 *
 * @param config - Original config object
 * @param isLegacyStepUp - Whether this is a legacy step_up node
 * @returns Normalized config with all required fields
 */
function normalizeWorkflowConfig(config: any, isLegacyStepUp: boolean, isLegacyDocv: boolean): any {
  const normalized = {
    ...config,
    workflow_token: config.workflow_token ?? null,
    workflow_type: config.workflow_type ?? null,
    workflow_version: config.workflow_current_version ?? config.workflow_version_id ?? null,
    is_workflow_version_active: config.workflow_current_version === null,
  };

  // Add legacy flag for step_up nodes transformed to workflow
  if (isLegacyStepUp) {
    normalized.legacy_step_up_node = true;
  }

  // Add legacy flag for docv nodes transformed to workflow
  if (isLegacyDocv) {
    normalized.legacy_docv_node = true;
  }

  return normalized;
}

/**
 * Normalize config for source_workflow nodes
 *
 * @param config - Original config object
 * @returns Normalized config with all required fields
 */
function normalizeSourceWorkflowConfig(config: any): any {
  return {
    ...config,
    workflow_token: config.workflow_token ?? config.application_token ?? null,
    workflow_type: config.workflow_type ?? null,
    workflow_version: config.application_version_id ?? config.workflow_current_version ?? null,
    is_source_workflow: true,
  };
}

/**
 * Normalize config for action nodes
 *
 * @param config - Original config object
 * @returns Normalized config with all required fields
 */
function normalizeActionConfig(config: any): any {
  return {
    ...config,
    action_id: config.journey_action_id ?? null,
    action_version_id: config.journey_action_version_id ?? null,
    action_name: config.action_name ?? null,
    action_type: config.action_type ?? null,
    action_version: config.action_version ?? null,
    event_type_title: config.event_type_title ?? null,
  };
}

/**
 * Normalize config for retry nodes
 *
 * @param config - Original config object
 * @returns Normalized config with all required fields
 */
function normalizeRetryConfig(config: any): any {
  return {
    ...config,
    retry_limit: config.retry_limit ?? null,
  };
}

/**
 * Normalize node config based on node type
 *
 * @param nodeType - The type of the node
 * @param config - The original config object
 * @param isLegacyStepUp - Whether this is a legacy step_up node
 * @param isLegacyDocv - Whether this is a legacy docv node
 * @returns Normalized config
 */
function normalizeNodeConfig(
  nodeType: DocNode['type'],
  config: any,
  isLegacyStepUp: boolean,
  isLegacyDocv: boolean,
): any {
  if (!config) return config;

  switch (nodeType) {
    case 'outcome':
      return normalizeOutcomeConfig(config);
    case 'review':
      return normalizeReviewConfig(config);
    case 'reconciliation_workflow':
      return normalizeReconciliationWorkflowConfig(config);
    case 'workflow':
    case 'start_router':
      return normalizeWorkflowConfig(config, isLegacyStepUp, isLegacyDocv);
    case 'source_workflow':
      return normalizeSourceWorkflowConfig(config);
    case 'action':
      return normalizeActionConfig(config);
    case 'retry':
      return normalizeRetryConfig(config);
    case 'addNodeForm':
    case 'start':
    case 'resume':
      // These nodes have no special config normalization
      return config || {};
    default:
      return config;
  }
}

/**
 * Converts an API node to document model format with complete field normalization
 *
 * This function handles:
 * - Type transformation (step_up -> workflow with legacy flag)
 * - Type transformation (docv -> workflow with legacy flag)
 * - Type transformation (end_user, event_action, etc. -> action with action_type in config)
 * - Outcome normalization
 * - Config field completion with null defaults
 *
 * @param node - The API node to convert
 * @returns The normalized document node
 */
export function mapApiNodeToDocNode(node: ApiNode): DocNode {
  // Transform step_up nodes to workflow nodes with legacy flag
  // Transform docv nodes to workflow nodes with legacy flag
  // Transform source_workflow nodes to workflow nodes with is_source_workflow flag
  // Transform action type nodes (end_user, event_action, etc.) to generic action nodes
  let nodeType = node.type;
  const isLegacyStepUp = node.type === 'step_up';
  const isLegacyDocv = node.type === 'docv';
  const isSourceWorkflow = node.type === 'source_workflow';
  const isActionNode = ['end_user', 'event_action', 'custom_review', 'parent_review', 'service'].includes(node.type);

  if (isLegacyStepUp || isLegacyDocv) {
    nodeType = 'workflow';
  } else if (isSourceWorkflow) {
    nodeType = 'source_workflow';
  } else if (isActionNode) {
    nodeType = 'action';
  }

  const base = {
    id: node.id,
    type: nodeType as DocNode['type'],
    name: (node as any).name ?? null,
  } as DocNode;

  // For action nodes, preserve the original API type in config.action_type
  let configToNormalize = node.config;
  if (isActionNode && configToNormalize) {
    configToNormalize = {
      ...configToNormalize,
      action_type: node.type, // Store original action type (event_action, end_user, etc.)
    };
  }

  // Normalize config based on node type
  const normalizedConfig = normalizeNodeConfig(
    nodeType as DocNode['type'],
    configToNormalize,
    isLegacyStepUp,
    isLegacyDocv,
  );

  const withConfig = { ...base, config: normalizedConfig } as DocNode;

  // Normalize outcomes
  // For action nodes, outcomes come from config.outcomes (array of strings)
  // For other nodes, outcomes come from node.outcomes (array of objects)
  let outcomes: DocNode['outcomes'];
  if (isActionNode && configToNormalize?.outcomes && Array.isArray(configToNormalize.outcomes)) {
    // Convert array of strings to outcome objects with labels
    outcomes = (configToNormalize.outcomes as string[]).map((outcomeLabel: string) => ({
      label: outcomeLabel,
    }));
  } else {
    outcomes = normalizeOutcomes(node.outcomes);
  }

  return outcomes && outcomes.length ? { ...withConfig, outcomes } : withConfig;
}

